import { forwardRef, type ElementRef, type PropsWithChildren } from "react";
import { format } from "date-fns";
import { HeartPulseIcon } from "lucide-react";

import type { Participation } from "@tudigo-monorepo/core-tudigo-api-models";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import {
  AccountCard,
  AlertRebrand,
  AlertRebrandTitle,
  Avatar,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogSeparator,
  DialogTitle,
  DialogTrigger,
} from "@tudigo-monorepo/web-tudigo-components";

import { ParticipationHealthStatus } from "../../../shared-external/components/participation-health-status";
import { ParticipationHoldingNameWithLinkedProjects } from "./participation-holding-name-with-linked-projects";

// Ref is automatically passed by the parent (DropdownMenuItem) somehow
// so we need to wrap it in a forwardRef to avoid an error
export const ParticipationLatestTudigoAnalysisDialog = forwardRef<
  ElementRef<typeof Dialog>,
  PropsWithChildren<{
    participation?: Participation;
    onOpenChange?: (open: boolean) => void;
  }>
>(({ children, participation, onOpenChange }) => {
  const { t } = useTranslation();

  if (!participation) return null;

  const { organization, analyst, health, review, reviewDate } = participation;

  const analystName =
    analyst?.firstname && analyst?.lastname
      ? `${analyst?.firstname} ${analyst?.lastname}`
      : "";

  return (
    <Dialog onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="z-dialog flex flex-col p-0 lg:max-w-[800px]">
        <DialogHeader className="h-[72px] w-full justify-start p-6">
          <DialogTitle className="body500-bold text-pretty p-0 text-black">
            {t(
              "participations.web_app.participations_list_page.participations_latest_tudigo_analysis_dialog.title",
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogSeparator />

        <div className="flex w-full flex-col space-y-4 p-4 lg:p-6">
          <AlertRebrand variant="info">
            <AlertRebrandTitle>
              <HeartPulseIcon className="size-5" />
              <ParticipationHoldingNameWithLinkedProjects
                organization={organization}
              />
            </AlertRebrandTitle>
          </AlertRebrand>
          <article className="flex flex-col gap-y-4 rounded-lg border border-neutral-200 bg-white p-6">
            <header className="flex w-full items-center justify-between">
              <AccountCard
                upperText={analystName}
                lowerText={analyst?.position ?? "Analyste @Tudigo"}
                avatar={
                  <Avatar
                    size="M"
                    url={analyst?.profileImage?.url ?? undefined}
                  >
                    {analystName}
                  </Avatar>
                }
                className="[&_p:first-child]:body200-medium [&_p:last-child]:body200-bold p-0 [&_p]:text-neutral-900"
              />
              <ParticipationHealthStatus health={health} />
            </header>
            <p className="body300-regular text-pretty text-neutral-900">
              {review ?? "Aucune analyse disponible"}
            </p>
          </article>
          <article className="flex items-center justify-between gap-y-4 rounded-lg border border-neutral-200 bg-white p-6">
            <p className="body300-regular text-neutral-900">
              {t(
                "participations.web_app.participations_list_page.participations_latest_tudigo_analysis_dialog.date",
              )}
            </p>
            <p className="body300-regular text-neutral-900">
              {reviewDate
                ? format(new Date(reviewDate), "dd/MM/yyyy")
                : "--/--/----"}
            </p>
          </article>
        </div>
      </DialogContent>
    </Dialog>
  );
});
